<script setup lang="ts">
import {
  UIModal,
  UICheckbox,
  UIInput,
  UIModalHeader,
  UIModalContent,
  UIModalFooter,
  UIContentWrap,
} from '@gohighlevel/ghl-ui'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void
  (e: 'onSubmit', data: { reasons: string[]; reasonBody: string }): void
}>()

const selectedReasons = ref<string[]>([])
const reasonBody = ref('')
const isReasonAdded = computed(() => {
  return selectedReasons.value.length > 0 || reasonBody.value.length > 0
})

//Keeping the value here the same as i18n string rather than an arbitary key so that on custom variables they carry the same locale
//Else we would have to manage translation key maps as well in backend
const reasons = [
  {
    label: t('common.notInterested'),
    value: t('common.notInterested'),
    key: 'not_interested',
  },
  { label: t('common.pricing'), value: t('common.pricing'), key: 'pricing' },
  {
    label: t('common.chooseAnotherVendor'),
    value: t('common.chooseAnotherVendor'),
    key: 'choose_another_vendor',
  },
  {
    label: t('common.timingNotSuitable'),
    value: t('common.timingNotSuitable'),
    key: 'timing_not_suitable',
  },
  { label: t('common.spam'), value: t('common.spam'), key: 'spam' },
]

const onCancel = () => {
  emit('update:show', false)
  selectedReasons.value = []
  reasonBody.value = ''
}

const onSubmit = () => {
  emit('onSubmit', {
    reasons: selectedReasons.value,
    reasonBody: reasonBody.value,
  })
}

const toggleReason = (value: string) => {
  const index = selectedReasons.value.indexOf(value)
  if (index === -1) {
    selectedReasons.value.push(value)
  } else {
    selectedReasons.value.splice(index, 1)
  }
}
</script>

<template>
  <UIModal :show="show" @update:show="onCancel" :width="520">
    <template #header>
      <UIModalHeader
        id="decline-modal-header"
        :title="t('documentViewer.specifyReason')"
        type="primary"
        @close="onCancel"
      />
    </template>

    <UIModalContent>
      <div class="mb-2 flex flex-col gap-2">
        <UICheckbox
          v-for="reason in reasons"
          :key="reason.value"
          :checked="selectedReasons.includes(reason.value)"
          @update:checked="() => toggleReason(reason.value)"
          :id="reason.value"
          class="!mb-0"
        >
          {{ reason.label }}
        </UICheckbox>
      </div>

      <UIContentWrap
        :locale="i18nLocale?.locale?.value || 'en-US'"
        class="p-0"
        style="display: contents"
      >
        <UIInput
          id="decline-reason-input"
          v-model="reasonBody"
          :placeholder="t('common.addReasonForDeclining')"
          type="textarea"
          :rows="4"
          :maxlength="300"
          :show-count="true"
          :scrollable="true"
        />
      </UIContentWrap>
    </UIModalContent>

    <template #footer>
      <UIModalFooter
        id="decline-modal-footer"
        :positive-text="t('common.submit')"
        :negative-text="t('common.cancel')"
        :disabled="loading || !isReasonAdded"
        @negativeClick="onCancel"
        @positiveClick="onSubmit"
        :loading="loading"
      />
    </template>
  </UIModal>
</template>
